import { Text } from "@shopify/polaris"
import { observer } from "mobx-react"
import React, { useLayoutEffect, useMemo, useRef, useState } from "react"

import { FormatPriceText } from "@/components"
import stores from "@/stores"

const BANNER_DESIGN_WIDTH = 1036

const getBannerDesignHeight = (recommendKind: number, statsCount: number) => {
  if (recommendKind === 2) return 106
  if (statsCount === 3) return 152
  return 120
}

const getBannerScale = (containerWidth: number) => {
  if (containerWidth <= 0) return null
  return Math.min(containerWidth / BANNER_DESIGN_WIDTH, 1)
}

const getFitScale = (innerWidth: number, maxWidth: number) => {
  if (innerWidth <= 0 || maxWidth <= 0) return 1
  return innerWidth > maxWidth ? maxWidth / innerWidth : 1
}

const applyFitScale = (
  el: HTMLElement | null,
  maxWidth: number,
  origin = "left center",
) => {
  if (!el) return
  el.style.transform = "none"
  const scale = getFitScale(el.scrollWidth, maxWidth)
  if (scale < 1) {
    el.style.transform = `scale(${scale})`
    el.style.transformOrigin = origin
  } else {
    el.style.transform = ""
    el.style.transformOrigin = ""
  }
}
const V6RecommendBannerPC = () => {
  const { PlanStore, UserInfoStore } = stores
  const user_pkg = PlanStore.config?.user_pkg
  const currencyFormat = UserInfoStore.data.currency_format
  const arr = [
    {
      label: "Retained Value",
      value: user_pkg?.back_in_store_value,
    },
    {
      label: "Upsell Value",
      value: user_pkg?.upsell_value,
    },
    {
      label: "Staff Cost Saved",
      value: user_pkg?.staff_cost_saved,
    },
  ].filter((item) => item.value)
  const length = arr.length

  const recommend_kind = user_pkg?.recommend_plan_kind ?? 0

  const designHeight = useMemo(
    () => getBannerDesignHeight(recommend_kind, length),
    [length, recommend_kind],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number | null>(null)

  const titleMaxWidth = length === 1 ? 380 : length === 2 ? 400 : 460
  const statsMaxWidth = length === 1 ? 280 : length === 2 ? 300 : 492

  useLayoutEffect(() => {
    setScale(null)
  }, [recommend_kind])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    let cancelled = false
    let rafId = 0

    const updateScale = () => {
      const nextScale = getBannerScale(container.getBoundingClientRect().width)
      if (nextScale !== null) {
        setScale(nextScale)
      }
      return nextScale !== null
    }

    const scheduleRetry = (attempt = 0) => {
      if (cancelled || attempt > 10) return
      rafId = requestAnimationFrame(() => {
        if (!updateScale() && !cancelled) {
          scheduleRetry(attempt + 1)
        }
      })
    }

    if (!updateScale()) {
      scheduleRetry()
    }

    const resizeObserver = new ResizeObserver(updateScale)
    resizeObserver.observe(container)
    window.addEventListener("resize", updateScale)

    return () => {
      cancelled = true
      if (rafId) cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateScale)
    }
  }, [designHeight, recommend_kind, length])

  useLayoutEffect(() => {
    applyFitScale(titleRef.current, titleMaxWidth, "left center")
    applyFitScale(statsRef.current, statsMaxWidth, "right center")
  }, [
    titleMaxWidth,
    statsMaxWidth,
    scale,
    length,
    recommend_kind,
    user_pkg?.back_in_store_value,
    user_pkg?.upsell_value,
    user_pkg?.staff_cost_saved,
  ])

  const ProfessionalOrEnterprice0 = (data) => (
    <div className="relative">
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const ProfessionalOrEnterprice1 = (data) => (
    <div className="relative [&_.Polaris-Text--root]:text-white [&_div]:text-[0px]">
      <div className="absolute h-full w-full flex gap-10 items-center">
        <div ref={titleRef} className="pl-[32px] h-full flex items-center">
          <div className="space-y-[6px]">
            <Text variant="headingXl" as="h2">
              {data.title}
            </Text>
            <div className="opacity-80">
              <Text variant="bodyMd" as="p">
                {data.subtitle}
              </Text>
            </div>
          </div>
        </div>
        <div
          ref={statsRef}
          className="flex gap-6 bg-black rounded-2xl px-[28px] py-[14px] absolute right-[386px]"
        >
          {arr.map((item) => {
            return (
              <div key={item.label} className="space-y-[2px]">
                <Text variant="headingXl" as="h2">
                  <FormatPriceText
                    value={item.value ?? 0}
                    currency={currencyFormat}
                  />
                </Text>
                <Text variant="bodyLg" as="p">
                  {item.label}
                </Text>
              </div>
            )
          })}
        </div>
      </div>
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const ProfessionalOrEnterprice2 = (data) => (
    <div className="relative [&_.Polaris-Text--root]:text-white">
      <div className="absolute h-full w-full flex gap-3 items-center">
        <div ref={titleRef} className="pl-[32px] h-full flex items-center">
          <div className="space-y-[6px]">
            <Text variant="headingXl" as="h2">
              {data.title}
            </Text>
            <Text variant="bodyMd" as="p" fontWeight="semibold">
              {data.subtitle}
            </Text>
          </div>
        </div>
        <div
          ref={statsRef}
          className="flex gap-6 bg-black rounded-2xl px-[28px] py-[14px] absolute right-[306px]"
        >
          {arr.map((item) => {
            return (
              <div key={item.label} className="space-y-[2px]">
                <Text variant="headingXl" as="h2">
                  <FormatPriceText
                    value={item.value ?? 0}
                    currency={currencyFormat}
                  />
                </Text>
                <Text variant="bodyLg" as="p">
                  {item.label}
                </Text>
              </div>
            )
          })}
        </div>
      </div>
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const ProfessionalOrEnterprice3 = (data) => (
    <div className="relative [&_.Polaris-Text--root]:text-white">
      <div className="absolute h-full w-full flex gap-3 items-center justify-between p-4">
        <div ref={titleRef} className="h-full flex items-center">
          <div className="space-y-2">
            <div className="px-3 py-1 bg-[#1274FF] rounded-lg inline-flex">
              <Text variant="bodyMd" as="span" fontWeight="semibold">
                {data.discount}
              </Text>
            </div>
            <Text variant="headingXl" as="h2">
              {data.title}
            </Text>
            <div className="opacity-80">
              <Text variant="bodyMd" as="p">
                {data.subtitle}
              </Text>
            </div>
          </div>
        </div>
        <div
          ref={statsRef}
          className="flex gap-4 bg-black rounded-2xl px-6 py-4 h-full"
        >
          {arr.map((item) => {
            return (
              <div key={item.label} className="space-y-[6px]">
                <Text variant="headingXl" as="h2">
                  <FormatPriceText
                    value={item.value ?? 0}
                    currency={currencyFormat}
                  />
                </Text>
                <Text variant="bodyLg" as="p">
                  {item.label}
                </Text>
              </div>
            )
          })}
        </div>
      </div>
      <img src={data.img} className="w-full flex" />
    </div>
  )

  const Content = () => {
    if (recommend_kind === 2) {
      return (
        <div className="relative">
          <div className="absolute pl-[60px] h-full  [&_.Polaris-Text--root]:text-white flex items-center ">
            <div className="space-y-[6px]">
              <Text variant="headingXl" as="h2">
                Start your Essential benefit{" "}
              </Text>
              <Text variant="bodyMd" as="p" fontWeight="semibold">
                Stores like yours recover $350 in retained revenue every month.
              </Text>
            </div>
          </div>
          <img
            src={
              "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_essential.png"
            }
            className="w-full flex"
          />
        </div>
      )
    }
    if (recommend_kind === 3) {
      if (length === 0) {
        return ProfessionalOrEnterprice0({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_full_professional.png",
        })
      }
      if (length === 1) {
        return ProfessionalOrEnterprice1({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_50.png",
          title: "Don't lose your Professional benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
      if (length === 2) {
        return ProfessionalOrEnterprice2({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_50.png",
          title: "Don't lose your Professional benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
      if (length === 3) {
        return ProfessionalOrEnterprice3({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_all.png",
          discount: "50% OFF FIRST MONTH",
          title: "Don't lose your Professional benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
    }
    if (recommend_kind === 4) {
      if (length === 0) {
        return ProfessionalOrEnterprice0({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_full_enterprise.png",
        })
      }
      if (length === 1) {
        return ProfessionalOrEnterprice1({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_30.png",
          title: "Don't lose your Enterprise benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
      if (length === 2) {
        return ProfessionalOrEnterprice2({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_30.png",
          title: "Don't lose your Enterprise benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
      if (length === 3) {
        return ProfessionalOrEnterprice3({
          img: "https://return-cdn.parcelpanel.com/public/tinified/v6_banner_empty_all.png",
          discount: "30% OFF FIRST MONTH",
          title: "Don't lose your Enterprise benefits",
          subtitle: "Over these days, ParcelWILL returns helped you to reached",
        })
      }
    }
  }

  const content = Content()
  if (!content) return null

  return (
    <div
      ref={containerRef}
      className="w-full overflow-hidden"
      style={{
        aspectRatio: `${BANNER_DESIGN_WIDTH} / ${designHeight}`,
      }}
    >
      <div
        className="origin-top-left"
        style={{
          width: BANNER_DESIGN_WIDTH,
          height: designHeight,
          transform: `scale(${scale ?? 1})`,
          visibility: scale === null ? "hidden" : "visible",
        }}
      >
        {content}
      </div>
    </div>
  )
}

export default observer(V6RecommendBannerPC)
